/**
 * <sf-action-videos> — UGC video carousel for the Shower Filter PDP.
 *
 * Scrolling is native CSS scroll-snap, so the track swipes and is keyboard
 * scrollable without this file. JS only adds arrow buttons, boundary state, and
 * playback: nothing loads video bytes until a play button is pressed, and only
 * one video plays at a time.
 */
class SfActionVideos extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-sf-track]');
    if (!this.track) return;

    this.cards = Array.from(this.querySelectorAll('[data-sf-video-card]'));
    this.prevBtn = this.querySelector('[data-sf-prev]');
    this.nextBtn = this.querySelector('[data-sf-next]');

    this.onScroll = this.#throttle(() => this.#updateArrows());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.prevBtn?.addEventListener('click', () => this.#step(-1));
    this.nextBtn?.addEventListener('click', () => this.#step(1));

    this.querySelectorAll('[data-sf-play]').forEach((btn) => {
      btn.addEventListener('click', () => this.#play(btn.closest('[data-sf-video-card]')));
    });

    // Stop playback once a card is scrolled well out of view, so audio never
    // continues from a card the shopper can no longer see.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting && e.target.hasAttribute('data-playing')) this.#stop(e.target);
        });
      },
      { root: this.track, threshold: 0.25 }
    );
    this.cards.forEach((c) => this.observer.observe(c));

    this.resizeObserver = new ResizeObserver(this.#throttle(() => this.#updateArrows()));
    this.resizeObserver.observe(this.track);

    this.#updateArrows();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  #throttle(fn) {
    let ticking = false;
    return () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        fn();
      });
    };
  }

  #cardStep() {
    const card = this.cards[0];
    if (!card) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  }

  #step(dir) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: dir * this.#cardStep(), behavior: reduce ? 'auto' : 'smooth' });
  }

  #updateArrows() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // Math.abs covers RTL, where scrollLeft is negative.
    const pos = Math.abs(scrollLeft);
    const atStart = pos <= 1;
    const atEnd = pos + clientWidth >= scrollWidth - 1;
    const noOverflow = scrollWidth <= clientWidth + 1;

    if (this.prevBtn) {
      this.prevBtn.disabled = atStart;
      this.prevBtn.hidden = noOverflow;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = atEnd;
      this.nextBtn.hidden = noOverflow;
    }
  }

  #stop(card) {
    if (!card) return;
    card.removeAttribute('data-playing');
    const video = card.querySelector('video');
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    // Removing the iframe is the only reliable way to stop an embed.
    const embed = card.querySelector('[data-sf-embed]');
    if (embed) embed.replaceChildren();
  }

  #play(card) {
    if (!card) return;
    this.cards.forEach((c) => {
      if (c !== card) this.#stop(c);
    });

    card.setAttribute('data-playing', '');

    const video = card.querySelector('video');
    if (video) {
      video.play()?.catch(() => {
        // Autoplay can still be refused; leave the poster and controls visible.
        card.removeAttribute('data-playing');
      });
      return;
    }

    const embed = card.querySelector('[data-sf-embed]');
    const src = card.dataset.embedUrl;
    if (embed && src && !embed.firstChild) {
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.title = card.dataset.embedTitle || 'Video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';
      embed.appendChild(iframe);
    }
  }
}

if (!customElements.get('sf-action-videos')) customElements.define('sf-action-videos', SfActionVideos);
